/**
 * Remote debug overlays (status / timers) are session-scoped.
 * Operator ON auto-arms a diagnostic session when none is active; on TTL expiry the
 * player clears only remoted-on overlays and ignores the same directive seq ON until
 * a newer operator action bumps the seq (or a fresh WS set-* arrives).
 */
import { PLAYER_DIAGNOSTIC_SESSION_MAX_TTL_SECONDS } from '@lucimark/shared/models/player-remote-diagnostics'
import {
  armPlayerDiagnosticSession,
  getPlayerDiagnosticSessionActiveUntilMs
} from '@/helpers/playerDiagnostics/playerDiagnosticsKillSwitch'

/** Matches Studio diagnostics “enable session” default (15 min). */
export const REMOTE_OVERLAY_DEFAULT_SESSION_TTL_SECONDS = 900 as const

let latchUntilMs = 0
let latchedTimers = false
let latchedStatus = false
/** Directive seqs ≤ this are refused for remote ON after an expiry clear. */
let expiredThroughDirectiveSeq = -1

function clampSessionTtlSeconds(seconds: number): number {
  return Math.min(
    PLAYER_DIAGNOSTIC_SESSION_MAX_TTL_SECONDS,
    Math.max(60, Math.floor(seconds))
  )
}

function ensureSessionAndLatch(nowMs: number): void {
  let until = getPlayerDiagnosticSessionActiveUntilMs()
  if (until <= nowMs) {
    until = nowMs + clampSessionTtlSeconds(REMOTE_OVERLAY_DEFAULT_SESSION_TTL_SECONDS) * 1000
    armPlayerDiagnosticSession(until)
  }
  latchUntilMs = Math.max(latchUntilMs, until)
}

/** Fresh WS set-* from the server (operator toggle / set). */
export function noteRemoteOverlayWsSet(
  axis: 'timers' | 'status',
  visible: boolean,
  nowMs = Date.now()
): void {
  if (visible) {
    ensureSessionAndLatch(nowMs)
    if (axis === 'timers') {
      latchedTimers = true
    }
    else {
      latchedStatus = true
    }
    return
  }
  if (axis === 'timers') {
    latchedTimers = false
  }
  else {
    latchedStatus = false
  }
  if (!latchedTimers && !latchedStatus) {
    latchUntilMs = 0
  }
}

/**
 * Filter a remote overlay directive before apply.
 * After expiry, ON for seq ≤ expiredThroughDirectiveSeq is ignored; OFF always applies.
 */
export function filterRemoteOverlayDirective(
  remote: { timersVisible?: boolean, statusVisible?: boolean },
  overlayUiDirectiveSeq: number,
  nowMs = Date.now()
): { timersVisible?: boolean, statusVisible?: boolean } {
  const refuseOn = overlayUiDirectiveSeq <= expiredThroughDirectiveSeq
  const out: { timersVisible?: boolean, statusVisible?: boolean } = {}

  if (remote.timersVisible !== undefined) {
    if (remote.timersVisible === false || !refuseOn) {
      out.timersVisible = remote.timersVisible
      noteRemoteOverlayWsSet('timers', remote.timersVisible, nowMs)
    }
  }
  if (remote.statusVisible !== undefined) {
    if (remote.statusVisible === false || !refuseOn) {
      out.statusVisible = remote.statusVisible
      noteRemoteOverlayWsSet('status', remote.statusVisible, nowMs)
    }
  }
  return out
}

export function expireRemoteOverlaysIfNeeded(
  nowMs: number,
  lastAppliedOverlayUiDirectiveSeq: number
): { clearTimers: boolean, clearStatus: boolean } {
  if ((latchedTimers || latchedStatus) && latchUntilMs > 0) {
    const sessionUntil = getPlayerDiagnosticSessionActiveUntilMs()
    if (sessionUntil > latchUntilMs) {
      latchUntilMs = sessionUntil
    }
  }
  if (latchUntilMs <= 0 || nowMs < latchUntilMs) {
    return { clearTimers: false, clearStatus: false }
  }
  const clearTimers = latchedTimers
  const clearStatus = latchedStatus
  latchedTimers = false
  latchedStatus = false
  latchUntilMs = 0
  if (Number.isInteger(lastAppliedOverlayUiDirectiveSeq) && lastAppliedOverlayUiDirectiveSeq >= 0) {
    expiredThroughDirectiveSeq = Math.max(expiredThroughDirectiveSeq, lastAppliedOverlayUiDirectiveSeq)
  }
  return { clearTimers, clearStatus }
}

/** Test helper — reset module state. */
export function resetRemoteOverlaySessionLatchForTests(): void {
  latchUntilMs = 0
  latchedTimers = false
  latchedStatus = false
  expiredThroughDirectiveSeq = -1
}
